// Componentes de los agentes de análisis (RR. HH., Productividad, Gerencia): personajes,
// sugerencias, chat, indicaciones y la burbuja flotante. Los usan la sala de control
// (DashboardPage) y la página de agentes.
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { nf, Modal } from './SalaControl';
import {
  analizarAgente,
  preguntarAgente,
  getHistorialAgente,
  getContextoAgente,
  getIndicacionesAgentes,
  guardarIndicacionAgente,
} from '../services/dashboard.services';

// ---------------------------------------------------------------------------
// Agentes de análisis por área
// ---------------------------------------------------------------------------

export const ESTILO_AGENTE = {
  rrhh: {
    color: 'var(--s3)',
    ejemplos: [
      '¿Qué centros concentran más horas extras y por qué?',
      '¿Quiénes trabajaron sin estar planificados esta semana?',
      '¿Cuántas horas faltan por autorizar y en qué centros?',
    ],
  },
  productividad: {
    color: 'var(--s1)',
    ejemplos: [
      '¿Qué centro bajó más su productividad frente a su promedio?',
      '¿Dónde hay más horas trabajadas sin más producción?',
      '¿Qué centros están sobre su límite de rechazo?',
    ],
  },
  gerencia: {
    color: 'var(--s2)',
    ejemplos: [
      '¿Cuáles son los 3 temas que requieren decisión esta semana?',
      '¿Cómo cerraría el mes en horas si sigue el ritmo actual?',
      '¿Qué centro tiene el mayor costo por kilo y por qué?',
    ],
  },
};

export const PRIORIDAD_SUG = {
  alta: { sev: 'var(--critical)', ink: 'var(--critical-ink)', texto: 'Alta' },
  media: { sev: 'var(--warning)', ink: 'var(--warn-ink)', texto: 'Media' },
  baja: { sev: 'var(--s1)', ink: 'var(--s1)', texto: 'Baja' },
};

export const fechaHora = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    timeZone: 'America/Bogota',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Personaje ilustrado de cada agente (SVG animado con CSS: respira, parpadea y,
// mientras analiza, inclina la cabeza y muestra puntos de "pensando").
const PERSONAJES = {
  // Recursos Humanos: mujer con diadema y micrófono.
  rrhh: {
    piel: '#e8b48f',
    pelo: '#4a2c1d',
    ropa: '#1baf7a',
    pelo_atras: <path d="M17 31c0-11 6-18 15-18s15 7 15 18v12c0 3-3 4-5 2l-1-8H23l-1 8c-2 2-5 1-5-2z" />,
    pelo_frente: <path d="M18 29c2-9 8-13 14-13 7 0 12 4 14 12-6-1-11-4-14-8-3 5-8 8-14 9z" />,
    cuerpo: null,
    cabeza: (
      <g fill="none" stroke="#2b3a4a" strokeWidth="2.2" strokeLinecap="round">
        <path d="M17.5 31c0-10 6.5-16 14.5-16s14.5 6 14.5 16" />
        <path d="M17 33v4M47 33v4M46 37c-2 5-6 7-10 7" />
        <circle cx="35" cy="44" r="1.6" fill="#2b3a4a" stroke="none" />
      </g>
    ),
  },
  // Productividad: persona con casco de seguridad.
  productividad: {
    piel: '#c98d63',
    pelo: '#2a2a2a',
    ropa: '#2a78d6',
    pelo_atras: <path d="M20 30c0-6 5-10 12-10s12 4 12 10v4H20z" />,
    pelo_frente: null,
    cabeza: (
      <g>
        <path d="M17 27c0-9 7-15 15-15s15 6 15 15z" fill="#f5b81c" />
        <rect x="14" y="25.5" width="36" height="4" rx="2" fill="#e3a40c" />
        <rect x="30" y="12.5" width="4" height="13" rx="1.5" fill="#e3a40c" />
      </g>
    ),
    cuerpo: <path d="M24 56l4 6M40 56l-4 6" stroke="#f5d24a" strokeWidth="3" strokeLinecap="round" />,
  },
  // Gerencia: hombre con gafas y corbata.
  gerencia: {
    piel: '#f0c7a2',
    pelo: '#3b3b46',
    ropa: '#2f3a4a',
    pelo_atras: <path d="M19 30c-1-10 5-17 13-17 9 0 14 6 13 17l-2-6H21z" />,
    pelo_frente: <path d="M20 25c3-7 8-9 13-9 6 0 10 3 12 9-7-3-15-3-25 0z" />,
    cabeza: (
      <g fill="none" stroke="#2b2b2b" strokeWidth="1.6">
        <circle cx="26.5" cy="33" r="4" />
        <circle cx="37.5" cy="33" r="4" />
        <path d="M30.5 33h3" />
      </g>
    ),
    cuerpo: (
      <g>
        <path d="M27 50l5 4 5-4-5 14z" fill="#eb6834" />
        <path d="M26 49l6 5 6-5" fill="none" stroke="#ffffff" strokeWidth="2" />
      </g>
    ),
  },
};

export const AvatarAgente = ({ area, tam = 40, analizando = false }) => {
  const e = ESTILO_AGENTE[area];
  const p = PERSONAJES[area];
  return (
    <span
      className={`agente-avatar persona${analizando ? ' analizando' : ''}`}
      style={{ '--ag': e.color, width: tam, height: tam }}
      aria-hidden="true"
    >
      <svg width={tam} height={tam} viewBox="0 0 64 64">
        <g className="persona-cuerpo">
          {/* Hombros */}
          <path d="M10 66c0-11 9-17 22-17s22 6 22 17z" fill={p.ropa} />
          <path d="M28 46h8v6c0 2-8 2-8 0z" fill={p.piel} />
          <g className="persona-cabeza">
            <g fill={p.pelo}>{p.pelo_atras}</g>
            <ellipse cx="32" cy="32" rx="11.5" ry="13" fill={p.piel} />
            <g fill={p.pelo}>{p.pelo_frente}</g>
            <g className="persona-ojos" fill="#2b2b2b">
              <circle cx="26.5" cy="33" r="1.6" />
              <circle cx="37.5" cy="33" r="1.6" />
            </g>
            <path d="M28 39.5c2.4 2 5.6 2 8 0" fill="none" stroke="#7a3b2e" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="23.5" cy="37.5" r="2" fill="#f08a80" opacity="0.35" />
            <circle cx="40.5" cy="37.5" r="2" fill="#f08a80" opacity="0.35" />
            {p.cabeza}
          </g>
          {p.cuerpo}
        </g>
        {analizando && (
          <g className="persona-pensando" fill="var(--ag)">
            <circle cx="47" cy="12" r="2.2" />
            <circle cx="53" cy="12" r="2.2" />
            <circle cx="59" cy="12" r="2.2" />
          </g>
        )}
      </svg>
    </span>
  );
};

// Los tres agentes juntos (botón flotante y cabecera del panel).
export const TrioAgentes = ({ tam = 30, analizando = false }) => (
  <span className="trio-agentes" style={{ '--tam': `${tam}px` }}>
    {['rrhh', 'gerencia', 'productividad'].map((area) => (
      <AvatarAgente key={area} area={area} tam={tam} analizando={analizando} />
    ))}
  </span>
);

const conteoPrioridades = (sugerencias = []) =>
  sugerencias.reduce((t, s) => ({ ...t, [s.prioridad]: (t[s.prioridad] || 0) + 1 }), {});

export const PildorasPrioridad = ({ sugerencias }) => {
  const c = conteoPrioridades(sugerencias);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {['alta', 'media', 'baja'].map((p) =>
        c[p] ? (
          <span key={p} className="pill" style={{ '--sev': PRIORIDAD_SUG[p].sev, '--sev-ink': PRIORIDAD_SUG[p].ink }}>
            <i className="dot" />
            {c[p]} {PRIORIDAD_SUG[p].texto.toLowerCase()}
          </span>
        ) : null,
      )}
    </div>
  );
};

export const ListaSugerencias = ({ analisis }) => {
  const [centro, setCentro] = useState('');
  const [prioridad, setPrioridad] = useState('');
  if (!analisis) return <div className="empty">Este agente aún no tiene un análisis.</div>;

  const centros = [...new Set(analisis.sugerencias.map((s) => s.centro))].sort();
  const visibles = analisis.sugerencias.filter(
    (s) => (!centro || s.centro === centro) && (!prioridad || s.prioridad === prioridad),
  );

  return (
    <>
      <div className="agente-resumen">{analisis.resumen}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <select className="ctl" value={centro} onChange={(e) => setCentro(e.target.value)} aria-label="Centro">
          <option value="">Todos los centros</option>
          {centros.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select className="ctl" value={prioridad} onChange={(e) => setPrioridad(e.target.value)} aria-label="Prioridad">
          <option value="">Todas las prioridades</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="baja">Baja</option>
        </select>
        <span className="eyebrow" style={{ marginLeft: 'auto' }}>
          {visibles.length} de {analisis.sugerencias.length} sugerencias
        </span>
      </div>
      <div className="sug-lista">
        {visibles.map((s, i) => {
          const p = PRIORIDAD_SUG[s.prioridad] || PRIORIDAD_SUG.baja;
          return (
            <article key={`${s.titulo}-${i}`} className="sug" style={{ '--sev': p.sev, '--sev-ink': p.ink }}>
              <header>
                <span className="pill">
                  <i className="dot" />
                  {p.texto}
                </span>
                <span className="sug-centro">{s.centro}</span>
              </header>
              <h4>{s.titulo}</h4>
              <p>{s.detalle}</p>
              <div className="sug-evidencia">
                <span className="eyebrow">Evidencia</span>
                {s.evidencia}
              </div>
              <div className="sug-accion">
                <strong>Acción: </strong>
                {s.accion}
              </div>
              {s.impactoEstimado && (
                <div className="sug-impacto">
                  <strong>Impacto estimado: </strong>
                  {s.impactoEstimado}
                </div>
              )}
            </article>
          );
        })}
      </div>
      {(analisis.datosFaltantes?.length > 0 || analisis.advertencias?.length > 0) && (
        <div className="plan-aviso" style={{ marginTop: 14 }}>
          <strong>Limitaciones de los datos</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {[...(analisis.advertencias || []), ...(analisis.datosFaltantes || [])].map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="card-sub" style={{ marginTop: 10 }}>
        Análisis {analisis.origen === 'cron' ? 'automático' : 'manual'} del {fechaHora(analisis.creado)} ·{' '}
        {analisis.modelo}
        {analisis.uso ? ` · ${nf(analisis.uso.inputTokens + analisis.uso.outputTokens)} tokens` : ''}
        {analisis.duracionMs ? ` · ${nf(analisis.duracionMs / 1000, 0)} s` : ''}. Las sugerencias las genera
        una IA a partir de los datos del sistema: verifícalas antes de actuar.
      </p>
    </>
  );
};

// Markdown mínimo de las respuestas del agente (negritas, viñetas, listas numeradas,
// títulos y tablas simples). Arma elementos de React, sin insertar HTML.
const enLinea = (texto, clave) =>
  String(texto)
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((parte, i) =>
      /^\*\*[^*]+\*\*$/.test(parte) ? (
        <strong key={`${clave}-${i}`}>{parte.slice(2, -2)}</strong>
      ) : (
        <span key={`${clave}-${i}`}>{parte}</span>
      ),
    );

const celdas = (linea) =>
  linea
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

export const TextoAgente = ({ texto }) => {
  const lineas = String(texto || '').split(/\r?\n/);
  const bloques = [];
  let i = 0;
  while (i < lineas.length) {
    const linea = lineas[i];
    if (!linea.trim()) {
      i += 1;
      continue;
    }
    // Tabla: filas con | y una línea separadora --- debajo del encabezado.
    if (/^\s*\|.*\|\s*$/.test(linea) && /^\s*\|?\s*:?-{3,}/.test(lineas[i + 1] || '')) {
      const encabezado = celdas(linea);
      const filas = [];
      i += 2;
      while (i < lineas.length && /^\s*\|.*\|\s*$/.test(lineas[i])) {
        filas.push(celdas(lineas[i]));
        i += 1;
      }
      bloques.push(
        <div key={`t${i}`} className="texto-agente-tabla">
          <table>
            <thead>
              <tr>{encabezado.map((c, j) => <th key={j}>{enLinea(c, `th${j}`)}</th>)}</tr>
            </thead>
            <tbody>
              {filas.map((f, r) => (
                <tr key={r}>{f.map((c, j) => <td key={j}>{enLinea(c, `td${r}-${j}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Lista con viñetas o numerada.
    const esVineta = /^\s*[-*•]\s+/;
    const esNumero = /^\s*\d+[.)]\s+/;
    if (esVineta.test(linea) || esNumero.test(linea)) {
      const patron = esVineta.test(linea) ? esVineta : esNumero;
      const items = [];
      while (i < lineas.length && patron.test(lineas[i])) {
        items.push(lineas[i].replace(patron, ''));
        i += 1;
      }
      const Lista = patron === esNumero ? 'ol' : 'ul';
      bloques.push(
        <Lista key={`l${i}`}>
          {items.map((it, j) => <li key={j}>{enLinea(it, `li${i}-${j}`)}</li>)}
        </Lista>,
      );
      continue;
    }
    // Título (#, ##, ###) o párrafo.
    const titulo = linea.match(/^\s*#{1,6}\s+(.*)$/);
    bloques.push(
      titulo ? (
        <p key={`h${i}`} className="texto-agente-titulo">{enLinea(titulo[1], `h${i}`)}</p>
      ) : (
        <p key={`p${i}`}>{enLinea(linea, `p${i}`)}</p>
      ),
    );
    i += 1;
  }
  return <div className="texto-agente">{bloques}</div>;
};

export const ChatAgente = ({ area, nombre, conversacion, setConversacion }) => {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const enviar = async (pregunta) => {
    const q = String(pregunta || '').trim();
    if (!q || enviando) return;
    setEnviando(true);
    setError(null);
    setTexto('');
    // Se reenvía la versión con códigos: los nombres reales no vuelven a la IA.
    const historial = conversacion.map((m) => ({ pregunta: m.pregunta, respuesta: m.respuestaCodificada }));
    setConversacion((prev) => [...prev, { pregunta: q, pendiente: true }]);
    try {
      const r = await preguntarAgente(area, q, historial);
      setConversacion((prev) => [
        ...prev.slice(0, -1),
        {
          pregunta: q,
          respuesta: r.respuesta,
          respuestaCodificada: r.respuestaCodificada,
          herramientas: r.herramientasUsadas,
          incompleta: r.incompleta,
        },
      ]);
    } catch (e) {
      setConversacion((prev) => prev.slice(0, -1));
      setTexto(q);
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="chat-agente">
      {!conversacion.length && (
        <div className="chat-ejemplos">
          <span className="eyebrow">Ejemplos</span>
          {ESTILO_AGENTE[area].ejemplos.map((ej) => (
            <button key={ej} type="button" className="ctl" onClick={() => enviar(ej)} disabled={enviando}>
              {ej}
            </button>
          ))}
        </div>
      )}
      <div className="chat-mensajes">
        {conversacion.map((m, i) => (
          <div key={i}>
            <div className="chat-burbuja usuario">{m.pregunta}</div>
            <div className="chat-burbuja agente">
              <AvatarAgente area={area} tam={24} />
              <div style={{ minWidth: 0 }}>
                {m.pendiente ? (
                  <span style={{ color: 'var(--ink-3)' }}>{nombre} está revisando los datos…</span>
                ) : (
                  <>
                    <TextoAgente texto={m.respuesta} />
                    {(m.herramientas?.length > 0 || m.incompleta) && (
                      <div
                        className="plan-sub"
                        style={{ marginTop: 6 }}
                        title={m.herramientas?.length ? m.herramientas.join('\n') : undefined}
                      >
                        {m.herramientas?.length > 0 &&
                          `Consultó ${m.herramientas.length} ${m.herramientas.length === 1 ? 'fuente' : 'fuentes'} de datos`}
                        {m.incompleta && ' · respuesta cortada por longitud'}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {error && <div className="plan-aviso">{error}</div>}
      <form
        className="chat-entrada"
        onSubmit={(e) => {
          e.preventDefault();
          enviar(texto);
        }}
      >
        <textarea
          value={texto}
          maxLength={1500}
          rows={2}
          placeholder={`Pregúntale al agente de ${nombre}…`}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar(texto);
            }
          }}
        />
        <button type="submit" className="ctl" disabled={enviando || !texto.trim()}>
          {enviando ? 'Pensando…' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

export const HistorialAgente = ({ area, onVer }) => {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getHistorialAgente(area, 20)
      .then(setLista)
      .catch((e) => setError(e.message));
  }, [area]);
  if (error) return <div className="empty">{error}</div>;
  if (!lista) return <div className="empty">Cargando…</div>;
  if (!lista.length) return <div className="empty">Sin análisis anteriores.</div>;
  return (
    <div className="tbl-scroll">
      <table className="data">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Origen</th>
            <th>Resultado</th>
            <th>Sugerencias</th>
            <th aria-label="Ver" />
          </tr>
        </thead>
        <tbody>
          {lista.map((a) => (
            <tr key={a.id}>
              <td>{fechaHora(a.creado)}</td>
              <td>{a.origen === 'cron' ? 'Automático' : 'Manual'}</td>
              <td style={a.estado === 'error' ? { color: 'var(--critical-ink)' } : undefined}>
                {a.estado === 'error' ? a.error : 'OK'}
              </td>
              <td>{a.estado === 'ok' ? <PildorasPrioridad sugerencias={a.sugerencias} /> : '—'}</td>
              <td>
                {a.estado === 'ok' && (
                  <button type="button" className="ctl" onClick={() => onVer(a)}>
                    Ver
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const DatosAgente = ({ area }) => {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getContextoAgente(area)
      .then(setCtx)
      .catch((e) => setError(e.message));
  }, [area]);
  if (error) return <div className="empty">{error}</div>;
  if (!ctx) return <div className="empty">Armando los datos que ve el agente…</div>;
  return (
    <>
      <p className="card-sub">
        Estos son los datos que recibe el agente ({nf(ctx.tokensAproximados)} tokens aprox.). Las personas
        van con códigos ({ctx.personasSeudonimizadas} en este análisis); los nombres y cédulas no salen
        del sistema.
      </p>
      <pre className="agente-json">{JSON.stringify(ctx.datos, null, 2)}</pre>
    </>
  );
};

export const AgenteModal = ({ agente, conversacion, setConversacion, onCerrar }) => {
  const [vista, setVista] = useState('sugerencias');
  const [analisisVisto, setAnalisisVisto] = useState(agente.analisis);
  const esAnterior = analisisVisto && agente.analisis && analisisVisto.id !== agente.analisis.id;

  return (
    <Modal
      titulo={`Agente de ${agente.nombre}`}
      subtitulo={agente.descripcion}
      onCerrar={onCerrar}
      ancho={980}
      acciones={<AvatarAgente area={agente.area} tam={32} />}
    >
      <div className="alert-tabs" role="group" aria-label="Vista del agente" style={{ padding: '0 0 12px' }}>
        {[
          ['sugerencias', 'Sugerencias'],
          ['preguntar', 'Preguntar'],
          ['historial', 'Historial'],
          ['datos', 'Datos que ve'],
        ].map(([clave, texto]) => (
          <button key={clave} type="button" className="tab" aria-pressed={vista === clave} onClick={() => setVista(clave)}>
            {texto}
          </button>
        ))}
      </div>
      {vista === 'sugerencias' && (
        <>
          {esAnterior && (
            <div className="plan-aviso" style={{ marginBottom: 10 }}>
              Estás viendo un análisis anterior ({fechaHora(analisisVisto.creado)}).{' '}
              <button type="button" className="ctl" onClick={() => setAnalisisVisto(agente.analisis)}>
                Ver el más reciente
              </button>
            </div>
          )}
          <ListaSugerencias key={analisisVisto?.id || 'vacio'} analisis={analisisVisto} />
        </>
      )}
      {vista === 'preguntar' && (
        <ChatAgente
          area={agente.area}
          nombre={agente.nombre}
          conversacion={conversacion}
          setConversacion={setConversacion}
        />
      )}
      {vista === 'historial' && (
        <HistorialAgente
          area={agente.area}
          onVer={(a) => {
            setAnalisisVisto(a);
            setVista('sugerencias');
          }}
        />
      )}
      {vista === 'datos' && <DatosAgente area={agente.area} />}
    </Modal>
  );
};

// Lista compacta para la burbuja: cada sugerencia se despliega al hacer clic.
const EJEMPLOS_INDICACION = {
  general:
    'Ej.: Responde siempre con el impacto en pesos cuando sea posible. Este mes la prioridad es bajar las horas extras.',
  rrhh: 'Ej.: Enfócate en las horas extras y en quienes trabajan sin estar planificados. Ignora a los aprendices.',
  productividad:
    'Ej.: Prioriza el consumo de químicos por kilo y la carga de las lavadoras. Compara solo centros hoteleros entre sí.',
  gerencia: 'Ej.: Resume en máximo 5 temas y di siempre cuánto cuesta cada uno al mes.',
};

// Editor de las indicaciones de un ámbito (general o un agente): texto y centros prioritarios.
export const EditorIndicacion = ({ titulo, descripcion, ambito, inicial, centros, maxCaracteres, onGuardado }) => {
  const { user } = useAuth();
  const [texto, setTexto] = useState(inicial?.texto || '');
  const [prioritarios, setPrioritarios] = useState(new Set(inicial?.centrosPrioritarios || []));
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const original = useMemo(
    () => JSON.stringify([inicial?.texto || '', [...(inicial?.centrosPrioritarios || [])].sort()]),
    [inicial],
  );
  const cambiado = JSON.stringify([texto.trim(), [...prioritarios].sort()]) !== original;

  const alternar = (id) =>
    setPrioritarios((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      const r = await guardarIndicacionAgente(ambito, {
        texto,
        centrosPrioritarios: [...prioritarios],
        actualizadoPor: {
          id: user?._id || user?.id || undefined,
          nombre: user?.nombre || user?.name || user?.email || undefined,
        },
      });
      onGuardado(r);
      setMensaje({ tipo: 'ok', texto: 'Guardado. Se aplica desde el próximo análisis o pregunta.' });
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.message });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="indicacion">
      <header>
        <h4>{titulo}</h4>
        <p className="plan-sub">{descripcion}</p>
      </header>
      <textarea
        value={texto}
        maxLength={maxCaracteres}
        rows={4}
        placeholder={EJEMPLOS_INDICACION[ambito]}
        onChange={(e) => setTexto(e.target.value)}
      />
      <div className="plan-sub" style={{ textAlign: 'right' }}>
        {texto.length} / {maxCaracteres}
      </div>
      <div className="eyebrow" style={{ margin: '6px 0 4px' }}>
        Centros que requieren mayor atención
      </div>
      <div className="indicacion-centros">
        {centros.map((c) => (
          <button
            key={c.id}
            type="button"
            className="indicacion-centro"
            aria-pressed={prioritarios.has(c.id)}
            onClick={() => alternar(c.id)}
          >
            {prioritarios.has(c.id) ? '★ ' : ''}
            {c.nombre}
          </button>
        ))}
      </div>
      <div className="indicacion-pie">
        <span className="plan-sub">
          {inicial?.actualizado && (inicial.texto || inicial.centrosPrioritarios?.length)
            ? `Última edición: ${inicial.actualizadoPor?.nombre || 'sin nombre'}, ${fechaHora(inicial.actualizado)}`
            : 'Sin indicaciones guardadas'}
        </span>
        <button type="button" className="ctl" onClick={guardar} disabled={guardando || !cambiado}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
      {mensaje && (
        <div className={mensaje.tipo === 'ok' ? 'indicacion-ok' : 'burbuja-error'} style={{ marginTop: 6 }}>
          {mensaje.texto}
        </div>
      )}
    </section>
  );
};

// Pestaña "Indicaciones" de la burbuja: las del agente seleccionado y las generales.
export const IndicacionesAgente = ({ area, nombreAgente }) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getIndicacionesAgentes()
      .then(setDatos)
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="burbuja-vacio">{error}</div>;
  if (!datos) return <div className="burbuja-vacio">Cargando indicaciones…</div>;

  const de = (ambito) => datos.indicaciones.find((i) => i.ambito === ambito);
  // Al guardar, el backend devuelve todas las indicaciones; se actualiza la vista.
  const actualizar = (r) => setDatos(r);

  return (
    <div className="indicaciones">
      <p className="plan-sub" style={{ margin: '4px 0 10px' }}>
        Indica en qué enfocarse, cómo responder y qué centros vigilar. Se guarda para todos los
        usuarios y se aplica en el análisis diario y en las preguntas.
      </p>
      <EditorIndicacion
        key={area}
        titulo={`Para ${nombreAgente}`}
        descripcion="Solo las usa este agente."
        ambito={area}
        inicial={de(area)}
        centros={datos.centros}
        maxCaracteres={datos.maxCaracteres}
        onGuardado={actualizar}
      />
      <EditorIndicacion
        key="general"
        titulo="Para todos los agentes"
        descripcion="Las usan Recursos Humanos, Productividad y Gerencia."
        ambito="general"
        inicial={de('general')}
        centros={datos.centros}
        maxCaracteres={datos.maxCaracteres}
        onGuardado={actualizar}
      />
    </div>
  );
};

export const SugerenciasCompactas = ({ analisis }) => {
  const [abierta, setAbierta] = useState(0);
  if (!analisis) {
    return <div className="burbuja-vacio">Este agente aún no tiene un análisis. Pulsa «Analizar ahora».</div>;
  }
  return (
    <>
      <div className="agente-resumen">{analisis.resumen}</div>
      <div className="burbuja-sugs">
        {analisis.sugerencias.map((s, i) => {
          const p = PRIORIDAD_SUG[s.prioridad] || PRIORIDAD_SUG.baja;
          const desplegada = abierta === i;
          return (
            <div key={`${s.titulo}-${i}`} className={`burbuja-sug${desplegada ? ' abierta' : ''}`} style={{ '--sev': p.sev, '--sev-ink': p.ink }}>
              <button type="button" className="burbuja-sug-cab" onClick={() => setAbierta(desplegada ? -1 : i)} aria-expanded={desplegada}>
                <i className="dot" style={{ background: p.sev }} />
                <span className="burbuja-sug-titulo">{s.titulo}</span>
                <span className="burbuja-sug-centro">{s.centro}</span>
              </button>
              {desplegada && (
                <div className="burbuja-sug-cuerpo">
                  <p>{s.detalle}</p>
                  <div className="sug-evidencia">
                    <span className="eyebrow">Evidencia</span>
                    {s.evidencia}
                  </div>
                  <div className="sug-accion">
                    <strong>Acción: </strong>
                    {s.accion}
                  </div>
                  {s.impactoEstimado && (
                    <div className="sug-impacto">
                      <strong>Impacto: </strong>
                      {s.impactoEstimado}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

/**
 * Agentes de análisis como burbuja flotante: un botón en la esquina inferior derecha
 * que abre un panel tipo chat con los tres agentes. "Ver todo" abre el modal completo
 * (historial y datos que ve el agente).
 */
export const AgentesBurbuja = ({ seccion, botonCentros }) => {
  const { data, error, recargar } = seccion;
  const [abierta, setAbierta] = useState(false);
  const [area, setArea] = useState('gerencia');
  const [vista, setVista] = useState('sugerencias');
  const [completo, setCompleto] = useState(null);
  const [errorAbierto, setErrorAbierto] = useState(false);
  const [analizando, setAnalizando] = useState({});
  const [errores, setErrores] = useState({});
  // Conversaciones por área mientras la página esté abierta.
  const [conversaciones, setConversaciones] = useState({});

  useEffect(() => {
    if (!abierta) return undefined;
    const alPresionar = (e) => e.key === 'Escape' && !completo && setAbierta(false);
    window.addEventListener('keydown', alPresionar);
    return () => window.removeEventListener('keydown', alPresionar);
  }, [abierta, completo]);

  const areas = data?.areas || [];
  const altas = (a) => (a.analisis?.sugerencias || []).filter((s) => s.prioridad === 'alta').length;
  const totalAltas = areas.reduce((t, a) => t + altas(a), 0);
  const algunoAnalizando = areas.some((a) => analizando[a.area] || a.analizando);
  const agente = areas.find((a) => a.area === area) || areas[0];

  const conversacionDe = (clave) => conversaciones[clave] || [];
  const setConversacionDe = (clave) => (fn) =>
    setConversaciones((prev) => ({
      ...prev,
      [clave]: typeof fn === 'function' ? fn(prev[clave] || []) : fn,
    }));

  const analizar = async (clave) => {
    setAnalizando((p) => ({ ...p, [clave]: true }));
    setErrores((p) => ({ ...p, [clave]: null }));
    try {
      await analizarAgente(clave);
      recargar();
    } catch (e) {
      setErrores((p) => ({ ...p, [clave]: e.message }));
    } finally {
      setAnalizando((p) => ({ ...p, [clave]: false }));
    }
  };

  const ocupado = agente && (analizando[agente.area] || agente.analizando);
  const agenteCompleto = areas.find((a) => a.area === completo);

  return createPortal(
    <div className="sala-control" style={{ background: 'transparent', minHeight: 0 }}>
      {abierta && (
        <section className="burbuja-panel" role="dialog" aria-label="Agentes de análisis">
          <header className="burbuja-cab">
            <span className="burbuja-logo">
              <TrioAgentes tam={22} analizando={algunoAnalizando} />
            </span>
            <div style={{ minWidth: 0 }}>
              <h3>Agentes IA</h3>
              <div className="plan-sub">
                {data ? `${data.nombreProveedor} · ${data.modelo}` : 'Cargando…'}
              </div>
            </div>
            <span className="spacer" />
            {botonCentros('agentes', true)}
            <Link
              className="ctl burbuja-icono"
              to="/dashboard/agentes"
              title="Configurar agentes: funciones, objetivos del mes, indicaciones y metas"
              aria-label="Configurar agentes"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
              </svg>
            </Link>
            {agente && (
              <button type="button" className="ctl burbuja-icono" title="Ver todo (historial y datos)" onClick={() => setCompleto(agente.area)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                  <path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" />
                </svg>
              </button>
            )}
            <button type="button" className="ctl burbuja-icono" onClick={() => setAbierta(false)} aria-label="Cerrar">
              ✕
            </button>
          </header>

          {error && <div className="burbuja-vacio">{error}</div>}
          {!data && !error && <div className="burbuja-vacio">Cargando agentes…</div>}

          {data && (
            <>
              <nav className="burbuja-agentes" aria-label="Elegir agente">
                {areas.map((a) => (
                  <button
                    key={a.area}
                    type="button"
                    className="burbuja-agente"
                    aria-pressed={a.area === agente?.area}
                    style={{ '--ag': ESTILO_AGENTE[a.area].color }}
                    onClick={() => setArea(a.area)}
                  >
                    <AvatarAgente area={a.area} tam={40} analizando={!!(analizando[a.area] || a.analizando)} />
                    <span>{a.nombre}</span>
                    {altas(a) > 0 && <b className="burbuja-insignia">{altas(a)}</b>}
                    {(analizando[a.area] || a.analizando) && <i className="burbuja-pulso" aria-hidden="true" />}
                  </button>
                ))}
              </nav>

              <div className="burbuja-cuerpo">
                {data.avisoConfiguracion && <div className="plan-aviso">{data.avisoConfiguracion}</div>}
                {!data.configurado && (
                  <div className="plan-aviso">
                    <strong>Agentes no configurados.</strong> Falta <code>{data.variableApiKey}</code> en{' '}
                    <code>empleados-back/.env</code> (ver <code>example.env</code>).
                  </div>
                )}

                {agente && (
                  <>
                    <div className="burbuja-estado">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{agente.nombre}</div>
                        <div className="plan-sub">
                          {agente.analisis ? `Analizado ${fechaHora(agente.analisis.creado)}` : 'Sin análisis todavía'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="ctl"
                        onClick={() => analizar(agente.area)}
                        disabled={ocupado || !data.configurado}
                        title={data.configurado ? 'Analizar con los datos de este momento (≈1 min)' : 'Falta configurar la API key'}
                      >
                        {ocupado ? 'Analizando…' : 'Analizar ahora'}
                      </button>
                    </div>
                    {(errores[agente.area] || agente.ultimoError) && (
                      <button
                        type="button"
                        className={`burbuja-error${errorAbierto ? ' abierto' : ''}`}
                        onClick={() => setErrorAbierto((v) => !v)}
                        title={errorAbierto ? 'Ver menos' : 'Ver el detalle'}
                      >
                        <span className="burbuja-error-texto">
                          {errores[agente.area] || `Último intento falló: ${agente.ultimoError.error}`}
                        </span>
                      </button>
                    )}

                    <div className="alert-tabs" role="group" aria-label="Vista" style={{ padding: 0 }}>
                      {[
                        ['sugerencias', 'Sugerencias'],
                        ['chat', 'Preguntar'],
                        ['indicaciones', 'Indicaciones'],
                      ].map(([clave, texto]) => (
                        <button key={clave} type="button" className="tab" aria-pressed={vista === clave} onClick={() => setVista(clave)}>
                          {texto}
                          {clave === 'sugerencias' && agente.analisis && <b>{agente.analisis.sugerencias.length}</b>}
                        </button>
                      ))}
                    </div>

                    <div className="burbuja-scroll">
                      {vista === 'sugerencias' ? (
                        <SugerenciasCompactas key={agente.analisis?.id || agente.area} analisis={agente.analisis} />
                      ) : vista === 'indicaciones' ? (
                        <IndicacionesAgente key={agente.area} area={agente.area} nombreAgente={agente.nombre} />
                      ) : (
                        <ChatAgente
                          area={agente.area}
                          nombre={agente.nombre}
                          conversacion={conversacionDe(agente.area)}
                          setConversacion={setConversacionDe(agente.area)}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
              <footer className="burbuja-pie">
                Análisis automático diario a las 6:00 a. m. · Sugerencias generadas con IA: verifícalas antes de actuar.
              </footer>
            </>
          )}
        </section>
      )}

      <button
        type="button"
        className={`burbuja-boton${abierta ? ' abierta' : ''}`}
        onClick={() => setAbierta((v) => !v)}
        aria-label={abierta ? 'Cerrar agentes' : 'Abrir agentes de análisis'}
        aria-expanded={abierta}
        title="Agentes IA: Recursos Humanos, Productividad y Gerencia"
      >
        {abierta ? <span style={{ fontSize: 20, lineHeight: 1 }}>✕</span> : <TrioAgentes tam={30} analizando={algunoAnalizando} />}
        {!abierta && totalAltas > 0 && <b className="burbuja-insignia">{totalAltas}</b>}
        {!abierta && algunoAnalizando && <i className="burbuja-pulso" aria-hidden="true" />}
      </button>

      {agenteCompleto && (
        <AgenteModal
          agente={agenteCompleto}
          conversacion={conversacionDe(agenteCompleto.area)}
          setConversacion={setConversacionDe(agenteCompleto.area)}
          onCerrar={() => setCompleto(null)}
        />
      )}
    </div>,
    document.body,
  );
};
